import { StateMachine } from './StateMachine';
import {
  MachineActionExec,
  MachineEvent,
  MachineServiceExec,
  MachineState,
  RestartOptions,
} from './types';
import { isAssignAction } from './util';

export class Interpreter<
  TContext = any,
  TEvent extends { type: string } = any,
> {
  machine: StateMachine<TContext, TEvent>;
  value: MachineState;
  context: TContext;
  onTransitionCallbacks: ((state: Interpreter) => void)[] = [];
  running = false;
  private listeners: Set<() => void> = new Set();
  private startCallbacks: Array<() => void> = [];
  private stopCallbacks: Array<() => void> = [];
  private currentSnapshot: { value: MachineState; context: TContext };
  private pendingEvents: (TEvent['type'] | TEvent)[] = [];
  private readonly definitionContext: TContext;
  private runGeneration = 0;

  constructor(
    machine: StateMachine<TContext, TEvent>,
    initialContext: Partial<TContext> = {}
  ) {
    this.machine = machine;
    this.value = machine.initialState;
    this.onTransitionCallbacks = [];

    this.definitionContext = { ...machine.context } as TContext;

    const fullInitialContext = {
      ...this.definitionContext,
      ...initialContext,
    } as TContext;

    this.context = fullInitialContext;
    this.updateContext(fullInitialContext);
    this.currentSnapshot = { value: this.value, context: this.context };
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  getSnapshot(): { value: MachineState; context: TContext } {
    return this.currentSnapshot;
  }

  start() {
    if (this.isRunning()) {
      throw new Error('Machine Interpreter is already running');
    }

    this.machine.start();
    this.running = true;

    this.startCallbacks.forEach((cb) => cb());

    this.handleImplicitTransitions();

    const queued = this.pendingEvents;
    this.pendingEvents = [];
    queued.forEach((event) => this.send(event));
  }

  stop() {
    this.runGeneration += 1;
    this.machine.stop();
    this.running = false;
    this.stopCallbacks.forEach((cb) => cb());
  }

  restart(options: RestartOptions<TContext> = {}) {
    const wasRunning = this.running;

    this.runGeneration += 1;
    this.pendingEvents = [];

    if (wasRunning) {
      this.stop();
    }

    this.value = this.machine.initialState;
    this.updateContext({
      ...this.definitionContext,
      ...options.initialContext,
    } as TContext);

    this.machine.inspector.clear();

    if (wasRunning) {
      const prevSnapshot = this.currentSnapshot;
      this.start();
      if (this.currentSnapshot === prevSnapshot) {
        this.handleOnTransitionCallbacks();
      }
    } else {
      this.handleOnTransitionCallbacks();
    }
  }

  send(providedEvent: TEvent['type'] | TEvent): void {
    const eventObject: MachineEvent<TEvent> =
      typeof providedEvent === 'string'
        ? ({ type: providedEvent } as TEvent)
        : providedEvent;

    if (!this.isRunning()) {
      this.pendingEvents.push(eventObject);
      return;
    }

    const { exec, ...transitionData } = this.machine.transition(
      this.value,
      eventObject
    );

    const prevValue = this.value;
    this.value = transitionData.state;

    const newContext = this.evaluateActions(
      transitionData.event,
      transitionData.actions || [],
      this.context
    );

    this.updateContext(newContext);

    /**
     * If we are in a service state, we need to evaluate the service and then
     * transition to the next state.
     */
    if (exec) {
      /**
       * If we're already in the same invokable service state, we don't need to
       * do anything because it's already running. So this is a noop.
       */
      if (prevValue === transitionData.state) {
        return this.finalizeServiceEntry();
      }

      const capturedGeneration = this.runGeneration;
      this.finalizeServiceEntry();
      this.evaluateService(exec, capturedGeneration).catch((error: any) => {
        console.error('Could not evaluate fsm service');
        console.error(error);
      });
    } else {
      this.finalizeTransition();
    }
  }

  evaluateService(
    serviceExec: MachineServiceExec<TContext, TEvent>,
    runGeneration = this.runGeneration
  ) {
    return serviceExec()
      .then((serviceData) => {
        if (runGeneration !== this.runGeneration) {
          return;
        }

        const { state, actions, event } = serviceData;

        const newServiceContext = this.evaluateActions(
          event,
          actions || [],
          this.context
        );

        this.updateContext(newServiceContext);
        this.value = state;

        this.finalizeTransition();
      })
      .catch((error) => {
        console.error('Could not evaluate fsm service');
        console.error(error);
      });
  }

  onTransition(callback: (state: Interpreter) => void) {
    this.onTransitionCallbacks.push(callback);
  }

  onStart(callback: () => void): () => void {
    this.startCallbacks.push(callback);
    return () => {
      const i = this.startCallbacks.indexOf(callback);
      if (i !== -1) this.startCallbacks.splice(i, 1);
    };
  }

  onStop(callback: () => void): () => void {
    this.stopCallbacks.push(callback);
    return () => {
      const i = this.stopCallbacks.indexOf(callback);
      if (i !== -1) this.stopCallbacks.splice(i, 1);
    };
  }

  /**
   * When a service state is entered, we want to call our on transition callbacks,
   * and potentially other stuff in the future, so that they get an update that we've
   * entered this new service state and the UI or any other observer can react accordingly.
   */
  finalizeServiceEntry() {
    this.handleOnTransitionCallbacks();
  }

  finalizeTransition(visited?: Set<MachineState>) {
    this.handleOnTransitionCallbacks();
    this.handleImplicitTransitions(visited);
  }

  /**
   * When we're in an invokable service or eventless transition state we want to kick off the
   * evaluation of that state after we've completed the current transition.
   */
  handleImplicitTransitions(visited: Set<MachineState> = new Set()) {
    const transitionData = this.machine.implicitTransition(this.value);

    if (!transitionData) {
      return;
    }

    if (transitionData.exec) {
      this.evaluateService(transitionData.exec, this.runGeneration).catch(
        (error: any) => {
          console.error('Could not evaluate fsm service');
          console.error(error);
        }
      );
    } else if (transitionData.state !== this.value) {
      if (visited.has(transitionData.state)) {
        throw new Error(
          `FSM: cycle detected in always transitions. ` +
            `Transitioning to "${transitionData.state}" would cause an infinite loop. ` +
            `Visited states: ${[...visited].join(' → ')} → ${this.value} → ${
              transitionData.state
            }`
        );
      }

      visited.add(this.value);
      this.value = transitionData.state;
      const newContext = this.evaluateActions(
        transitionData.event,
        transitionData.actions || [],
        this.context
      );

      this.updateContext(newContext);
      this.finalizeTransition(visited);
    }
  }

  handleOnTransitionCallbacks() {
    this.currentSnapshot = { value: this.value, context: this.context };
    this.onTransitionCallbacks.forEach((callback) => {
      callback(this);
    });
    this.listeners.forEach((listener) => listener());
  }

  evaluateActions(
    event: MachineEvent<TEvent>,
    actions: MachineActionExec<TContext, TEvent>[],
    context: TContext
  ) {
    let newContext = { ...context };

    if (!actions) {
      return newContext;
    }

    actions.forEach((action) => {
      const actionResult = action.exec(newContext, event);

      if (isAssignAction(actionResult)) {
        const dataToMerge = actionResult.data || {};
        newContext = { ...newContext, ...dataToMerge };
      }
    });

    return newContext;
  }

  matches(state: MachineState) {
    return this.value === state;
  }

  hasTag(tag: string) {
    return this.machine.hasTag(this.value, tag);
  }

  hasTagForState(value: MachineState, tag: string) {
    return this.machine.hasTag(value, tag);
  }

  isRunning() {
    return this.running;
  }

  updateContext(newContext: TContext) {
    this.context = newContext;
    this.machine.setContext(newContext);
  }

  mergeContext(newContext: Partial<TContext> = {}) {
    this.updateContext({ ...this.context, ...newContext });

    // Notify useSyncExternalStore subscribers but not onTransitionCallbacks —
    // a context merge is not a state transition.
    if (this.running) {
      this.currentSnapshot = { value: this.value, context: this.context };
      this.listeners.forEach((listener) => listener());
    }
  }

  get history() {
    return this.machine.inspector.getTimeline();
  }

  getInspector() {
    return this.machine.inspector;
  }
}
